"use client"

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { Skeleton } from '@/shared/components/ui/skeleton'

interface MessagesAccessGuardProps {
    children: React.ReactNode
}

export default function MessagesAccessGuard({ children }: MessagesAccessGuardProps) {
    const { session, isLoading } = useSession()
    const router = useRouter()
    const [isChecking, setIsChecking] = useState(true)
    const [hasAccess, setHasAccess] = useState(false)

    useEffect(() => {
        // Wait for session to load
        if (isLoading) {
            return
        }

        // Check if we have a session
        if (!session) {
            router.replace('/')
            return
        }

        // Check if user is admin
        if (session.role !== UserRole.ADMIN) {
            router.replace('/home')
            return
        }

        // User is admin, allow access
        setHasAccess(true)
        setIsChecking(false)
    }, [session, isLoading, router])

    // Show loading state while checking
    if (isLoading || isChecking) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-full max-w-md space-y-4">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Checking access...</p>
                    </div>
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                    </div>
                </div>
            </div>
        )
    }

    // Only render children if user has access
    if (!hasAccess) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Redirecting...</p>
                </div>
            </div>
        )
    }

    return <>{children}</>
}
